import React, { useEffect, useState } from "react";
import "../common/AddNewLicensePanel.css";
import Icon from "../../../common/Icon";
import { toast } from "react-toastify";
import PageLoader from "../../../shared/PageLoader";
import { getUserDetails } from "../../../../backend/auth";
import { useCreateLicenseType } from "../../../../backend/license";
import Select, { components } from "react-select";
import { useGetUserCompanies } from "../../../../backend/query";
import { useGetStates, useGetCities } from "../../../../backend/masters";

interface DropdownOption {
  id: string | number;
  name: string;
}

interface WatcherOption {
  id: string | number;
  name: string;
  designation: string;
}

interface SidePanelProps {
  isOpen: boolean;
  closePanel: () => void;
  onSuccess?: () => void;
  selectCity?: DropdownOption[];
  selectState?: DropdownOption[];
  watchers?: WatcherOption[];
  onChange?: (filters: any) => void;
  showStateCityInsteadOfLocation?: boolean;
}

interface FormData {
  license_type_name: string;
  authority: string;
  company: string;
  assigned_to_user_id: string;
  // escalation_user_id: string;
  watcher_ids: string;
  default_validity_days: number | "";
  renewal_days: number | "";
  sop_new: string;
  sop_renewal: string;
  sop_amendment: string;
  sop_surrender: string;
  state: string;
  city: string;
  [key: string]: string | number | ""
}


const AddNewLicenseTypePanel: React.FC<SidePanelProps> = ({
  isOpen,
  closePanel,
  onSuccess,
  selectState = [],
  selectCity = [],
  onChange,
  showStateCityInsteadOfLocation = false,
}) => {

  const user = getUserDetails();

  const { createLicenseType, creating } = useCreateLicenseType(
    (response: any) => {
      toast.success("New license type added successfully", {
        icon: <Icon name="snackbarSuccess" />,
      });
      resetForm();
      closePanel();
      if (onSuccess) onSuccess();
    },
    (error: any) => {
      const errorData = error && error.response && error.response.data;
      const errorMessages = errorData && errorData.error_messages;

      if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
        errorMessages.forEach((errMsg: string) => {
          toast.error(errMsg, {
            icon: <Icon name="snackbarError" />,
          });
        });
      } else if (errorData && errorData.message) {
        toast.error(errorData.message);
      } else {
        toast.error("Failed to add license type");
      }
    }
  );

  const [formData, setFormData] = useState<FormData>({
    license_type_name: "",
    authority: "",
    company: "",
    assigned_to_user_id: "",
    // escalation_user_id: "",
    watcher_ids: "",
    default_validity_days: "",
    renewal_days: "",
    sop_new: "",
    sop_renewal: "",
    sop_amendment: "",
    sop_surrender: "",
    state: "",
    city: "",
  });

  const [currentStep, setCurrentStep] = useState(1);

  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState({
    new: true,
    renewal: false,
    amendment: false,
    surrender: false,
  });

  const ALL_OPTION = {
    value: "all",
    label: "ALL",
    company: null,
    associate_company: null,
    location: null,
    state: null,
    city: null,
  };

  const [reminderCount, setReminderCount] = useState<number>(0);
  const [validationError, setValidationError] = useState<string>("");
  const [validityError, setValidityError] = useState<string>("");
  const [selectedScope, setSelectedScope] = useState<string>("");
  const [isPerpetual, setIsPerpetual] = useState(false);

  const [company, setCompany] = useState<any>(null);
  const [associateCompany, setAssociateCompany] = useState<any>(null);
  
  const [companies, setCompanies] = useState<any[]>([]);
  const [associateCompanies, setAssociateCompanies] = useState<any[]>([]);
  const [state, setState] = useState<any>(null);
  const [city, setCity] = useState<any>(null);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const { states: allStates, isFetching: loadingStates } = useGetStates({}, true);
  const { cities: allCities, isFetching: loadingCities } = useGetCities(
    state && state.value ? { filters: { state_id_eq: state.value } } : {},
    Boolean(state && state.value)
  );
  const [reminderDays, setReminderDays] = useState<number[]>([]);

  // const [showStateCityInsteadOfLocationInternal, setShowStateCityInsteadOfLocationInternal] = useState(false);

  const { userCompanies, isFetching } = useGetUserCompanies(
    {
      include: [
        "associate_companies",
        "associate_companies.locations",
      ],
      includes: [
        "associate_companies",
        "associate_companies.locations",
      ],
    },
    true
  );

 

  // Update array when reminderCount changes
  useEffect(() => {
    if (reminderCount === 0) {
      setReminderDays([]);
    } else if (reminderDays.length !== reminderCount) {
      const newReminderDays = [...reminderDays];
      if (newReminderDays.length < reminderCount) {
        // Add empty values for new fields - no default values
        while (newReminderDays.length < reminderCount) {
          newReminderDays.push(0);
        }
      } else {
        // Trim excess values
        newReminderDays.length = reminderCount;
      }
      setReminderDays(newReminderDays);
    }
  }, [reminderCount]);

  // Replace dropdowns with number inputs
  {[...Array(reminderCount)].map((_, i) => (
    <div key={i}>
      <label>Renewal Reminder {i + 1} to be sent on (days before expiry)</label>
      <input
        type="number"
        placeholder="Number of days before expiry"
        value={reminderDays[i] || ""}
        onChange={(e) => {
          const newReminderDays = [...reminderDays];
          newReminderDays[i] = Number(e.target.value);
          setReminderDays(newReminderDays);
        }}
      />
    </div>
  ))}

  // Use user input in payload (replace defaultReminderDays)
  const payload = {
    license_type: {
      // ... other fields
      renewal_reminder_count: reminderCount,
      reminder_days: reminderDays,  // Use user input instead of defaults
    }
  };

  useEffect(() => {
    if (userCompanies && userCompanies.length > 0) {
      const companyOptions = [
        ...userCompanies.map(function (c: any) {
          return { value: c.id, label: c.name, company: c };
        }),
      ];
      setCompanies(companyOptions);
    }
  }, [userCompanies]);

  useEffect(() => {
    if (allStates && allStates.length > 0) {
      const stateOptions = allStates.map(function (s: any) {
        return { value: s.id, label: s.name, state: s };
      });
      setStates(stateOptions);
    }
  }, [allStates]);

  useEffect(() => {
    if (Array.isArray(allCities) && allCities.length > 0) {
      const cityOptions = allCities.map(function (c: any) {
        return { value: c.id, label: c.name, city: c };
      });
      setCities(cityOptions);
    } else {
      setCities([]);
    }
  }, [JSON.stringify(allCities)]);

  useEffect(() => {
    setCity(null);
  }, [state]);
  
 useEffect(() => {
    if (company && company.company) {
      const assocCompanies = [
        ...(company.company.associate_companies || []).map(function (ac: any) {
          return {
            value: ac.id,
            label: ac.name,
            associate_company: ac,
            locations: ac.locations || [],
          };
        }),
      ];
      setAssociateCompanies(assocCompanies);
      setAssociateCompany(null);
    } else {
      setAssociateCompanies([]);
      setAssociateCompany(null);
    }
  }, [company]);


  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    const { name, value } = target;
    const isCheckbox = target instanceof HTMLInputElement && target.type === "checkbox";
    const checkedValue = isCheckbox ? target.checked : value;

    setFormData((prev: any) => ({
      ...prev,
      [name]: checkedValue,
    }));
  };

  const resetForm = () => {
    setFormData({
      license_type_name: "",
      authority: "",
      company: "",
      assigned_to_user_id: "",
      // escalation_user_id: "",
      watcher_ids: "",
      default_validity_days: "",
      renewal_days: "",
      sop_new: "",
      sop_renewal: "",
      sop_amendment: "",
      sop_surrender: "",
      state: "",
      city: "",
    });

    setChecked({
      new: true,
      renewal: false,
      amendment: false,
      surrender: false,
    });

    setReminderCount(0);
    setReminderDays([]);
    setValidationError("");
    setValidityError("");
    setSelectedScope("");
    setIsPerpetual(false);
    setCurrentStep(1);

    setCompany(null);
    setAssociateCompany(null);
    setState(null);
    setCity(null);
    setCities([]);
  };

  // Handle close panel with reset
  const handleClosePanel = () => {
    resetForm();
    closePanel();
  };

  const validateForm = () => {
    const errors: string[] = [];
    if (!formData.license_type_name || !formData.license_type_name.trim()) {
      errors.push("License Type Name is required.");
    }
    if (!formData.authority || !formData.authority.trim()) {
      errors.push("Authority is required.");
    }
    if (!company || !company.value) {
      errors.push("Company is required.");
    }
    if (selectedScope === "location") {
      if (!state || !state.value) {
        errors.push("State is required when scope is Location.");
      }
    }
    if (!Object.values(checked).some(Boolean)) {
      errors.push("At least one Standard Operating Procedure option must be selected.");
    } else {
      Object.entries(checked).forEach(([key, value]) => {
        if (value) {
          const sop = formData[`sop_${key}`] || "";
          if (!sop) {
            errors.push(`Standard Operating Procedure description for ${key.charAt(0).toUpperCase() + key.slice(1)} is required.`);
          }
        }
      });
    }

    // Validate renewal days against default validity days (in days)
    if (
      formData.default_validity_days !== "" &&
      formData.renewal_days !== "" &&
      Number(formData.renewal_days) >= Number(formData.default_validity_days)
    ) {
      errors.push("Up for Renewal Days must be less than Default Validity Days.");
    }

    // Validate renewal reminder days for duplicates
    if (reminderCount > 0 && reminderDays.length > 0) {
      const filledDays = reminderDays.filter(day => day !== undefined && day !== null && day !== 0);

      // Check if all reminder days are filled
      if (filledDays.length < reminderCount) {
        errors.push("Please fill all renewal reminder days.");
      }

      // Check for duplicate days
      const uniqueDays = new Set(filledDays);
      if (uniqueDays.size !== filledDays.length) {
        errors.push("Renewal reminder days must be unique. Please remove duplicate values.");
      }
    }

    return errors;
  };

  const onSubmit = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach((err) =>
        toast.error(err, { icon: <Icon name="snackbarError" /> })
      );
      return;
    }
    const licenseCode = formData.license_type_name
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_|_$/g, "")
      .substring(0, 50) || "LICENSE_TYPE";
    // const actionsEnabled = Object.entries(checked)
    //   .filter(([_, isChecked]) => isChecked)
    //   .map(([action, _]) => action);
    const sopData: Record<string, string> = {};
    Object.entries(checked).forEach(([key, value]) => {
      if (value) {
        sopData[key] = String(formData[`sop_${key}`] || "");
      }
    });
    const scopeValue = selectedScope || "central";

    const payload = {
      license_type: {
        code: licenseCode,
        name: formData.license_type_name,
        authority: formData.authority,
        scope: scopeValue,
        is_active: true,
        // actions_enabled: actionsEnabled,
        company_id: company && company.company ? company.company.id.toString() : "",
        associate_company_id: associateCompany && associateCompany.associate_company ? associateCompany.associate_company.id.toString() : "",
        state_id:
          selectedScope === "location" && state && state.value
            ? state.value.toString()
            : "",
        city_id:
          selectedScope === "location" && city && city.value
            ? city.value.toString()
            : "",
        perpetual_allowed: isPerpetual,

        sop: sopData,
        ...(isPerpetual ? {} : {
              default_validity_days: Number(formData.default_validity_days),
              up_for_renewal_days: Number(formData.renewal_days),
              renewal_reminder_count: reminderCount,
              reminder_days: reminderDays,
            }),
        created_by_id: user.user_id.toString(),
        updated_by_id: user.user_id.toString(),
      },
    };
    createLicenseType(payload as any);
  };


  const validateRenewalDays = (renewalValue: number | "", validityValue: number | "") => {
    if (renewalValue === "" || validityValue === "") {
      setValidationError("");
      return;
    }

    if (renewalValue > validityValue) {
      setValidationError(
        "Up for Renewal Days should be less than Default Validity Days."
      );
    } else {
      setValidationError("");
    }
  };

  const DropdownIndicator = function (props: any) {
    return (
      <components.DropdownIndicator {...props}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
        >
          <path
            d="M1 1L5 5L9 1"
            stroke="#1E1E1E"
            strokeWidth="1.33333"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </components.DropdownIndicator>
    );
  };

  return (
    <>
      {isOpen && <div className="sidepanel-overlay" onClick={handleClosePanel}></div>}
      <div className="sidepanel-wrapper">
        <div className={`sidepanel ${isOpen ? "open" : ""}`}>
          <div className="sidepanel-header d-flex justify-content-between align-items-center pb-4">
            <h5 className="mb-0 fw-semibold">Create License Type</h5>
            <button
              className="btn-close"
              aria-label="Close"
              onClick={handleClosePanel}
            ></button>
          </div>

          <div className="sidepanel-body h-100">
            {currentStep === 1 && (
              <form className="d-flex flex-column justify-content-between">
                <div>
                  <div className="mb-3">
                    <label className="form-label">
                      License Type Name<span>*</span>
                    </label>
                    <input
                      className="form-textfield"
                      type="text"
                      name="license_type_name"
                      placeholder="Add License Type Name"
                      value={formData.license_type_name || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="mb-3 d-flex align-items-center gap-5">
                    <div>
                      <label className="mb-0 me-auto form-label">Is Perpetual</label>
                      <p className="mb-0 me-2 text-gray-12">
                        Enable this toggle if this license does not expire
                      </p>
                    </div>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="isPerpetual"
                        checked={isPerpetual}
                        onChange={() => {
                          setIsPerpetual((prev) => !prev);
                          if (!isPerpetual) {
                            setValidityError("");
                          }
                        }}
                      />
                    </div>
                  </div>

                  {!isPerpetual && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">
                          Default Validity (in Days)<span>*</span>
                        </label>
                        <input
                          className="form-control"
                          type="number"
                          placeholder="Add in number of days"
                          // min={1}
                          // max={100}
                          value={formData.default_validity_days}
                          onChange={(e) => {
                            const raw = e.target.value;
                            let value: number | "" = raw === "" ? "" : Number(raw);
                            if (value !== "" && value > 1000) {
                              value = 1000;
                            }
                            setFormData((prev) => ({
                              ...prev,
                              default_validity_days: value,
                            }));
                            validateRenewalDays(
                              Number(formData.renewal_days),
                              value === "" ? "" : value
                            );
                          }}
                        />
                        {validityError && (
                          <small className="text-danger mt-1 mb-0 text-xsm">
                            {validityError}
                          </small>
                        )}
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Up for Renewal (in Days)<span>*</span></label>
                        <input
                            className="form-control"
                            type="number"
                            min={0}
                            max={
                              formData.default_validity_days === ""
                                ? undefined
                                : Number(formData.default_validity_days) - 1
                            }
                            placeholder="Enter number of days before expiry"
                            value={formData.renewal_days}
                            onChange={(e) => {
                              const raw = e.target.value;
                              let value: number | "" = raw === "" ? "" : Number(raw);
                              if (
                                value !== "" &&
                                formData.default_validity_days !== "" &&
                                value >= Number(formData.default_validity_days)
                              ) {
                                value = Number(formData.default_validity_days) - 1;
                              }

                              setFormData((prev) => ({
                                ...prev,
                                renewal_days: value,
                              }));

                              validateRenewalDays(
                                value,
                                Number(formData.default_validity_days)
                              );
                            }}
                          />
                        {validationError && (
                          <small className="text-danger mt-1 mb-0 text-xsm">
                            {validationError}
                          </small>
                        )}
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Number of Renewal Reminders<span>*</span></label>
                        <select
                          className="form-select select-placeholder"
                          value={reminderCount}
                          onChange={(e) => setReminderCount(Number(e.target.value))}
                        >
                          <option value="">Select number of reminders</option>
                          {[3, 4, 5].map((num) => (
                            <option key={num} value={num}>
                              {num}
                            </option>
                          ))}
                        </select>
                      </div>

                      {reminderCount > 0 && (
                        <div className="ps-4 mb-3 mt-3">
                          {[...Array(reminderCount)].map((_, i) => (
                            <div key={i} className="mb-3">
                              <label className="mb-1 text-md">
                                Renewal Reminder {i + 1} – to be sent before expiry date
                              </label>
                              <select
                                className="form-select"
                                value={reminderDays[i] || ""}
                                onChange={(e) => {
                                  const newReminderDays = [...reminderDays];
                                  newReminderDays[i] = Number(e.target.value);
                                  setReminderDays(newReminderDays);
                                }}
                              >
                                <option value="" disabled>
                                  Select number of days before the expiry date
                                </option>
                                {[...Array(1000)].map((_, index) => (
                                  <option key={index + 1} value={index + 1}>
                                    {index + 1}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  <div className="mb-3">
                    <label className="mb-1 text-md">Scope</label>
                    <div className="d-flex gap-2 mb-2">
                      <input
                        type="radio"
                        id="central"
                        name="scope"
                        value="central"
                        checked={selectedScope === "central"}
                        onChange={() => {
                          setSelectedScope("central");
                          setFormData((prev) => ({
                            ...prev,
                            state: "",
                            city: "",
                          }));
                        }}
                      />
                      <label htmlFor="central">Central</label>
                    </div>

                    <div className="d-flex gap-2">
                      <input
                        type="radio"
                        id="location"
                        name="scope"
                        value="location"
                        checked={selectedScope === "location"}
                        onChange={() => setSelectedScope("location")}
                      />
                      <label htmlFor="location">Location</label>
                    </div>

                    {selectedScope === "location" && (
                      <div className="ps-4 mb-3 mt-3">
                        <div className="mb-3">
                          <label className="mb-1 text-md form-label">
                            State<span>*</span>
                          </label>
                          <Select
                            placeholder="State"
                            options={states}
                            onChange={setState}
                            value={state}
                            components={{ DropdownIndicator }}
                            isLoading={loadingStates}
                            isDisabled={selectedScope !== "location"}
                          />
                        </div>
                        <div>
                          <label className="mb-1 text-md">City</label>
                          <Select
                            placeholder="City"
                            options={cities}
                            onChange={setCity}
                            value={city}
                            components={{ DropdownIndicator }}
                            isLoading={loadingCities}
                            isDisabled={!state || !state.value || selectedScope !== "location"}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Authority<span>*</span>
                    </label>
                    <input 
                      className="form-textfield" 
                      type="text" 
                      name="authority"
                      value={formData.authority}
                      onChange={handleInputChange}
                      placeholder="Add Authority Name"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Company<span>*</span>
                    </label>
                    <Select
                      placeholder="Company"
                      options={companies}
                      value={company}
                      onChange={setCompany}
                      components={{ DropdownIndicator }}
                      isLoading={isFetching}
                      className="select-control"
                      />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Associate Company<span>*</span></label>
                    <Select
                      placeholder="Associate Company"
                      options={associateCompanies}
                      onChange={setAssociateCompany}
                      value={associateCompany}
                      components={{ DropdownIndicator }}
                      isDisabled={!company || !company.value}
                      className="select-control"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Standard Operating Procedure<span>*</span>
                    </label>

                    {[
                      { key: "new", label: "New" },
                      { key: "renewal", label: "Renewal" },
                      { key: "amendment", label: "Amendment" },
                      { key: "surrender", label: "Surrender" },
                    ]
                      .filter(({ key }) => !(key === "renewal" && isPerpetual))
                      .map(({ key, label }) => (
                        <div key={key} className="d-flex flex-column gap-2">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <input
                              className="form-check"
                              type="checkbox"
                              id={key}
                              checked={checked[key as keyof typeof checked] || false}
                              onChange={() =>
                                setChecked((prev) => ({
                                  ...prev,
                                  [key]: !prev[key as keyof typeof prev],
                                }))
                              }
                            />
                            <label htmlFor={key} className="form-check-label">
                              <div className="fs-6">{label}</div>
                            </label>
                          </div>

                          {checked[key as keyof typeof checked] && (
                            <textarea
                              name={`sop_${key}`}
                              className="mb-2"
                              rows={2}
                              placeholder={`Add standard operating procedure for ${label}`}
                              value={formData[`sop_${key}`] || ""}
                              onChange={handleInputChange}
                            />
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
        <div className={`button-container ${isOpen ? "open" : ""}`}>
          <div className="d-flex gap-3 w-100">
            <button
              type="button"
              className="button-secondary w-100"
              onClick={handleClosePanel}
              disabled={creating}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button-primary w-100"
              onClick={onSubmit}
              disabled={creating}
            >
              Create
            </button>
          </div>
        </div>
      </div>
      {creating && <PageLoader />}
    </>
  );
};

export default AddNewLicenseTypePanel;